import { PropsWithChildren } from 'react';
import { AxisScaleOutput } from '@visx/axis';
import { ParentSize } from '@visx/responsive';
import { ScaleConfig } from '@visx/scale';
import { DatumObject } from '@visx/shape/lib/types';
import { AnimatedAxis, DataProvider, TooltipProvider, XYChart, XYChartTheme } from '@visx/xychart';

import { cn } from '../../../style-system/cn';
import { BaseChartProps as SharedProps } from '../charts.model';
import { formatDate, startCase } from '../common/formatters';

import { Legend } from './legend';

const defaultProps = {
    axis: {
        hideTicks: true,
        tickLabelProps: {
            fill: 'var(--color-content-deselected)',
        },
    },
};

export interface BaseChartProps<T extends DatumObject> extends SharedProps<T>, PropsWithChildren {
    theme: XYChartTheme;
    xScale: ScaleConfig<AxisScaleOutput, any, any>;
    yScale: ScaleConfig<AxisScaleOutput, any, any>;
    legend?: boolean;
    height: number;
    width?: number;
    xAxis?: boolean;
    yAxis?: boolean;
    isDisabled: (key: keyof T) => boolean;
    onKeyToggle: (key: keyof T) => void;
    margin?: { top: number; bottom: number; left: number; right: number };
}

export const BaseChart = <T extends DatumObject>({
    children,
    xScale,
    yScale,
    theme,
    formatY,
    formatX = formatDate,
    formatLabel = startCase,
    legend = true,
    width: initialWidth,
    height: initialHeight = 300,
    xAxis = true,
    yAxis = true,
    onKeyToggle,
    isDisabled,
    margin = { top: 50, bottom: 50, left: 50, right: 50 },
}: BaseChartProps<T>) => (
    <div
        style={{
            minHeight: `${initialHeight}px`,
            width: initialWidth ? `${initialWidth}px` : '100%',
        }}
    >
        <TooltipProvider hideTooltipDebounceMs={0}>
            <DataProvider xScale={xScale} yScale={yScale} theme={theme}>
                <ParentSize>
                    {({ width }) =>
                        width ? (
                            <div className={cn('relative', !!legend && 'pb-4')}>
                                <XYChart margin={margin} height={initialHeight} width={width}>
                                    {yAxis && (
                                        <AnimatedAxis
                                            {...defaultProps.axis}
                                            orientation="left"
                                            tickFormat={formatY}
                                            numTicks={Math.ceil(initialHeight / 64)}
                                        />
                                    )}
                                    {xAxis && (
                                        <AnimatedAxis
                                            {...defaultProps.axis}
                                            orientation="bottom"
                                            tickFormat={formatX}
                                            numTicks={Math.ceil(width / 100)}
                                        />
                                    )}
                                    {children}
                                </XYChart>
                                {legend && (
                                    <Legend
                                        isDisabled={isDisabled}
                                        onKeyToggle={onKeyToggle}
                                        formatLabel={formatLabel}
                                    />
                                )}
                            </div>
                        ) : null
                    }
                </ParentSize>
            </DataProvider>
        </TooltipProvider>
    </div>
);
